import { ComponentPortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Inject, HostBinding, Input, NgModule, Injectable } from '@angular/core';
import { _getShadowRoot } from '@angular/cdk/platform';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';

class GpuLoadingSpinnerComponent {
    static { this.styleNodeDiameters = new Set(); }
    get viewBox() {
        return `0 0 ${this.diameter} ${this.diameter}`;
    }
    get circleRadius() {
        return this.diameter / 2 - this.strokeWidth;
    }
    get strokeWidthPercentage() {
        return (this.strokeWidth / this.diameter) * 100;
    }
    get strokeCircumference() {
        return 2 * Math.PI * this.circleRadius;
    }
    constructor(document, elementRef) {
        this.document = document;
        this.elementRef = elementRef;
        this.loadingSpinnerClass = true;
        this.diameter = 96;
        this.strokeWidth = 4;
        this.styleRoot = _getShadowRoot(this.elementRef.nativeElement) || this.document.head;
    }
    ngOnInit() {
        this.attachStyleNode();
    }
    ngOnChanges(changes) {
        if (changes.diameter) {
            this.attachStyleNode();
        }
    }
    attachStyleNode() {
        const styleNodeDiameters = GpuLoadingSpinnerComponent.styleNodeDiameters;
        if (!styleNodeDiameters.has(this.diameter)) {
            const styleNode = this.document.createElement('style');
            styleNode.textContent = this.circleAnimation(this.diameter, this.strokeCircumference);
            this.styleRoot.appendChild(styleNode);
            styleNodeDiameters.add(this.diameter);
        }
    }
    circleAnimation(diameter, strokeCircumference) {
        const start = (1 - 0.05) * strokeCircumference;
        const end = (1 - 0.8) * strokeCircumference;
        return `@keyframes gpu-loading-spinner-stroke-rotate-${diameter} {
      0%       { stroke-dashoffset: ${start}; transform: rotate(0); }
      12.5%    { stroke-dashoffset: ${end};   transform: rotate(0); }
      12.5001% { stroke-dashoffset: ${end};   transform: rotateX(180deg) rotate(72.5deg); }
      25%      { stroke-dashoffset: ${start}; transform: rotateX(180deg) rotate(72.5deg); }
      25.0001% { stroke-dashoffset: ${start}; transform: rotate(270deg); }
      37.5%    { stroke-dashoffset: ${end};   transform: rotate(270deg); }
      37.5001% { stroke-dashoffset: ${end};   transform: rotateX(180deg) rotate(161.5deg); }
      50%      { stroke-dashoffset: ${start}; transform: rotateX(180deg) rotate(161.5deg); }
      50.0001% { stroke-dashoffset: ${start}; transform: rotate(180deg); }
      62.5%    { stroke-dashoffset: ${end};   transform: rotate(180deg); }
      62.5001% { stroke-dashoffset: ${end};   transform: rotateX(180deg) rotate(251.5deg); }
      75%      { stroke-dashoffset: ${start}; transform: rotateX(180deg) rotate(251.5deg); }
      75.0001% { stroke-dashoffset: ${start}; transform: rotate(90deg); }
      87.5%    { stroke-dashoffset: ${end};   transform: rotate(90deg); }
      87.5001% { stroke-dashoffset: ${end};   transform: rotateX(180deg) rotate(341.5deg); }
      100%     { stroke-dashoffset: ${start}; transform: rotateX(180deg) rotate(341.5deg); }
    }`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerComponent, deps: [{ token: DOCUMENT }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuLoadingSpinnerComponent, selector: "gpu-loading-spinner", inputs: { diameter: "diameter", strokeWidth: "strokeWidth", color: "color" }, host: { properties: { "class.gpu-loading-spinner": "this.loadingSpinnerClass" } }, usesOnChanges: true, ngImport: i0, template: "<svg\n  [style.width.px]=\"diameter\"\n  [style.height.px]=\"diameter\"\n  [attr.viewBox]=\"viewBox\"\n  preserveAspectRatio=\"xMidYMid meet\"\n  focusable=\"false\"\n  aria-hidden=\"true\"\n>\n  <circle\n    cx=\"50%\"\n    cy=\"50%\"\n    [attr.r]=\"circleRadius\"\n    [style.stroke-width.%]=\"strokeWidthPercentage\"\n    [style.stroke-dasharray.px]=\"strokeCircumference\"\n    [style.animation-name]=\"'gpu-loading-spinner-stroke-rotate-' + diameter\"\n    [style.stroke]=\"color\"\n  ></circle>\n</svg>\n", styles: [".gpu-loading-spinner svg{animation:gpu-loading-spinner-rotate 2s linear infinite}.gpu-loading-spinner circle{fill:transparent;stroke:#00b9bb;transform-origin:center;transition-property:stroke;animation-duration:4s;animation-timing-function:linear;animation-iteration-count:infinite}@keyframes gpu-loading-spinner-rotate{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-loading-spinner', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<svg\n  [style.width.px]=\"diameter\"\n  [style.height.px]=\"diameter\"\n  [attr.viewBox]=\"viewBox\"\n  preserveAspectRatio=\"xMidYMid meet\"\n  focusable=\"false\"\n  aria-hidden=\"true\"\n>\n  <circle\n    cx=\"50%\"\n    cy=\"50%\"\n    [attr.r]=\"circleRadius\"\n    [style.stroke-width.%]=\"strokeWidthPercentage\"\n    [style.stroke-dasharray.px]=\"strokeCircumference\"\n    [style.animation-name]=\"'gpu-loading-spinner-stroke-rotate-' + diameter\"\n    [style.stroke]=\"color\"\n  ></circle>\n</svg>\n", styles: [".gpu-loading-spinner svg{animation:gpu-loading-spinner-rotate 2s linear infinite}.gpu-loading-spinner circle{fill:transparent;stroke:#00b9bb;transform-origin:center;transition-property:stroke;animation-duration:4s;animation-timing-function:linear;animation-iteration-count:infinite}@keyframes gpu-loading-spinner-rotate{0%{transform:rotate(0)}to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ElementRef }], propDecorators: { loadingSpinnerClass: [{
                type: HostBinding,
                args: ['class.gpu-loading-spinner']
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], color: [{
                type: Input
            }] } });

class GpuLoadingSpinnerOverlayComponent {
    constructor() {
        this.loadingSpinnerClass = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuLoadingSpinnerOverlayComponent, selector: "gpu-loading-spinner-overlay", host: { properties: { "class.gpu-loading-spinner-overlay": "this.loadingSpinnerClass" } }, ngImport: i0, template: "<div class=\"w-100 h-100 d-flex align-items-center justify-content-center\">\n  <gpu-loading-spinner></gpu-loading-spinner>\n</div>\n", styles: [".gpu-loading-spinner-overlay{width:100%}\n"], dependencies: [{ kind: "component", type: GpuLoadingSpinnerComponent, selector: "gpu-loading-spinner", inputs: ["diameter", "strokeWidth", "color"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-loading-spinner-overlay', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"w-100 h-100 d-flex align-items-center justify-content-center\">\n  <gpu-loading-spinner></gpu-loading-spinner>\n</div>\n", styles: [".gpu-loading-spinner-overlay{width:100%}\n"] }]
        }], propDecorators: { loadingSpinnerClass: [{
                type: HostBinding,
                args: ['class.gpu-loading-spinner-overlay']
            }] } });

class GpuLoadingSpinnerWrapperComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerWrapperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuLoadingSpinnerWrapperComponent, selector: "gpu-loading-spinner-wrapper", ngImport: i0, template: "<div class=\"d-flex justify-content-center p-5\">\n  <ng-content select=\"gpu-loading-spinner\"></ng-content>\n</div>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingSpinnerWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-loading-spinner-wrapper', template: "<div class=\"d-flex justify-content-center p-5\">\n  <ng-content select=\"gpu-loading-spinner\"></ng-content>\n</div>\n" }]
        }] });

class GpuLoadingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingModule, declarations: [GpuLoadingSpinnerComponent, GpuLoadingSpinnerWrapperComponent, GpuLoadingSpinnerOverlayComponent], imports: [CommonModule, OverlayModule], exports: [GpuLoadingSpinnerComponent, GpuLoadingSpinnerWrapperComponent, GpuLoadingSpinnerOverlayComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingModule, imports: [CommonModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuLoadingSpinnerComponent, GpuLoadingSpinnerWrapperComponent, GpuLoadingSpinnerOverlayComponent],
                    imports: [CommonModule, OverlayModule],
                    exports: [GpuLoadingSpinnerComponent, GpuLoadingSpinnerWrapperComponent, GpuLoadingSpinnerOverlayComponent],
                }]
        }] });

class GpuLoadingService {
    constructor(overlay) {
        this.overlay = overlay;
    }
    ngOnDestroy() {
        this.overlayRef?.dispose();
    }
    show() {
        if (this.overlayRef) {
            return;
        }
        this.overlayRef = this.overlay.create({
            width: '100%',
            height: '100%',
            hasBackdrop: true,
            backdropClass: 'gpu-loading-spinner-overlay-backdrop',
        });
        const loadingSpinner = new ComponentPortal(GpuLoadingSpinnerOverlayComponent);
        this.overlayRef.attach(loadingSpinner);
    }
    hide() {
        this.overlayRef?.dispose();
        this.overlayRef = null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingService, deps: [{ token: i1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingService, providedIn: GpuLoadingModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuLoadingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: GpuLoadingModule }]
        }], ctorParameters: () => [{ type: i1.Overlay }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GpuLoadingModule, GpuLoadingService, GpuLoadingSpinnerComponent, GpuLoadingSpinnerOverlayComponent, GpuLoadingSpinnerWrapperComponent };
